import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Badge,
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { getDisplayableAppointmentNotes } from "../../../../helpers/appointmentNotesDisplay";
import {
  AGENDA_RESOURCE_PAGE_SIZE,
  buildAgendaSearchParams,
  buildWeekDayDates,
  clampResourcePage,
  formatIsoDate,
  paginateIds,
  parseAgendaDateParam,
  parseResourcePageParam,
  readAgendaCalendarView,
  readAgendaResourcePage,
  writeAgendaCalendarView,
  writeAgendaResourcePage,
} from "../../../../helpers/agendaCalendarStorage";
import {
  addDaysToIsoDate,
  snapMinutesToSlot,
} from "../../../../helpers/agendaDayGrid";
import type { AgendaGridBlock, AgendaGridColumn } from "../../../../helpers/agendaGridTypes";
import {
  mergeCalendarSlotBounds,
  ClosedCalendarDayMeta,
  mergeClosedCalendarDates,
  mergeClosedCalendarDayMeta,
  readConsolidatedViewPreference,
  writeConsolidatedViewPreference,
} from "../../../../helpers/agendaConsolidatedView";
import {
  getAgendaCalendarViewForTenant,
  getAgendaProfessionalIdsForTenant,
  getAgendaShowInactiveForTenant,
  getAgendaLastOptionsActionForTenant,
  getAgendaListLateOnlyForTenant,
  getAgendaListOnlyWithExtrasForTenant,
  getAgendaTabForTenant,
  getAgendaUnitForTenant,
  resolveCurrentTenantId,
  setAgendaCalendarViewForTenant,
  setAgendaLastOptionsActionForTenant,
  setAgendaProfessionalIdsForTenant,
  setAgendaShowInactiveForTenant,
  setAgendaListLateOnlyForTenant,
  setAgendaListOnlyWithExtrasForTenant,
  setAgendaTabForTenant,
  setAgendaUnitForTenant,
  type AgendaOptionsActionPreference,
} from "../../../../helpers/agendaUnitContext";
import { ADMIN_ROLES, canConcludeAppointments, canMarkClientArrived } from "../../../../Routes/roleAccess";
import { getAuthRoleNames } from "../../../../services/authService";
import AgendaCustomGrid, { AgendaCustomGridHandle } from "./AgendaCustomGrid";
import AgendaOptionsMenu from "./AgendaOptionsMenu";
import AgendaProfessionalFilterDropdown from "./AgendaProfessionalFilterDropdown";
import AgendaStatusLegend from "./AgendaStatusLegend";
import AgendaWeekDayStrip from "./AgendaWeekDayStrip";
import { defaultAbsenceRange } from "./absenceRange";
import AppointmentDetailOffcanvas from "./AppointmentDetailOffcanvas";
import AppointmentFormOffcanvas, {
  AppointmentCreatePrefill,
  AppointmentFormMode,
} from "./AppointmentFormOffcanvas";
import WaitingRoomPanel from "./WaitingRoomPanel";
import WaitingRoomTvOverlay from "./WaitingRoomTvOverlay";
import PixPaymentModal from "../../../../Components/Studio/PixPaymentModal";
import ClientAnamnesisOffcanvas from "../../../../Components/Studio/ClientAnamnesisOffcanvas";
import WaitlistOffcanvas from "../../../../Components/Studio/WaitlistOffcanvas";
import { canViewClinicalRecords } from "../../../../helpers/clinicalRecordAccess";
import { playArrivedChime, isLateActiveAppointment, isLateArrivableAppointment } from "../../../../helpers/arrivedFeedback";
import {
  promptFillHealthRecordIfMissing,
  appointmentNeedsHealthRecord,
  confirmConcludeDespitePreattendGap,
} from "../../../../helpers/promptFillHealthRecord";
import {
  appointmentToBatchHealthQueueItem,
  promptContinueBatchHealthQueue,
  type BatchHealthQueueItem,
} from "../../../../helpers/promptBatchHealthRecordQueue";
import {
  confirmClinicalGateContinue,
  isClinicalGateAckRequired,
  extractClinicalGateAlertsFromError,
} from "../../../../helpers/promptClinicalGate";
import ScheduleBlocksModal from "../Professionals/ScheduleBlocksModal";
import {
  addWallClockMinutes,
  formatWallClockTime,
  todayIsoDate,
} from "../../../../helpers/agendaDateTime";
import {
  getHelpArticleUrl,
  HELP_ARTICLE_AGENDA,
  HELP_ARTICLE_UNIT_SCHEDULE,
} from "../../../../helpers/helpCenter";
import {
  Appointment,
  clearClientArrived,
  confirmDeposit,
  getAll,
  getById,
  listAppointments,
  markClientArrived,
  update,
  updateStatus,
} from "../../../../services/appointmentService";
import {
  getProfessionals,
  Professional,
} from "../../../../services/professionalService";
import { getUnitCalendarBounds } from "../../../../services/unitCalendarBoundsService";
import { getUnitSchedules } from "../../../../services/unitScheduleService";
import { PaginationMeta } from "../../../../types/api";
import { getUnits, Unit } from "../../../../services/unitService";
import { listWaitingRoom } from "../../../../services/waitingRoomService";

const PER_PAGE = 15;
const CALENDAR_FETCH_PER_PAGE = 100;
const CALENDAR_EXIT_ANIMATION_MS = 320;
const DEFAULT_EVENT_COLOR = "#405189";
type CalendarDateRange = {
  start_date: string;
  end_date: string;
};

const DEFAULT_CALENDAR_BOUNDS = {
  slotMinTime: "08:00:00",
  slotMaxTime: "20:00:00",
  scrollTime: "08:00:00",
};

const buildUnitSchedulePath = (unitId: string): string =>
  unitId ? `/units?schedule=${unitId}` : "/units";

const formatSlotBoundLabel = (time: string): string => {
  const hours = time.slice(0, 2);
  const minutes = time.slice(3, 5);

  return minutes === "00" ? `${hours}h` : `${hours}h${minutes}`;
};

const ACTIONABLE_APPOINTMENT_STATUSES = ["scheduled", "confirmed", "pending_deposit"] as const;

/** Aligned with backend `AvailabilityService` — inactive slots must not block the grid visually. */
const HIDDEN_CALENDAR_STATUSES = new Set(["canceled", "expired", "no_show"]);

const isBundleAppointment = (appointment: Appointment): boolean =>
  Boolean(appointment.service_bundle_id || appointment.bundle_group_id);

const isInactiveCalendarStatus = (status: string): boolean => HIDDEN_CALENDAR_STATUSES.has(status);

const AgendaList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  document.title = t("studio.agenda.pageTitle");

  const formatDateLabel = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(i18n.language, { dateStyle: "full" }).format(new Date(`${value}T12:00:00`)),
    [i18n.language]
  );

  const [listAppointmentsData, setListAppointmentsData] = useState<Appointment[]>([]);
  const [calendarAppointments, setCalendarAppointments] = useState<Appointment[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [filterDate, setFilterDate] = useState(todayIsoDate());
  const [filterUnitId, setFilterUnitId] = useState("");
  const refreshWaitingRoomBadge = useCallback(async () => {
    if (!filterUnitId) {
      setWaitingRoomBadgeCount(0);
      return;
    }

    try {
      const response = await listWaitingRoom({
        unit_id: Number(filterUnitId),
        date: filterDate,
      });
      setWaitingRoomBadgeCount(
        typeof response.meta?.waiting_count === "number"
          ? response.meta.waiting_count
          : Array.isArray(response.data)
            ? response.data.filter(
                (entry) => entry.waiting_room_status === "waiting"
              ).length
            : 0
      );
    } catch {
      // Keep last known badge; avoid toast noise on background poll.
    }
  }, [filterDate, filterUnitId]);
  const [listOnlyWithExtras, setListOnlyWithExtras] = useState(() => {
    return getAgendaListOnlyWithExtrasForTenant(resolveCurrentTenantId()) ?? false;
  });
  const [listLateOnly, setListLateOnly] = useState(() => {
    return getAgendaListLateOnlyForTenant(resolveCurrentTenantId()) ?? false;
  });
  const [calendarRange, setCalendarRange] = useState<CalendarDateRange | null>(null);
  const [selectedProfessionalIds, setSelectedProfessionalIds] = useState<number[]>([]);
  const listOnlyWithExtrasId = useId();
  const listLateOnlyId = useId();

  const listAppointmentsWithExtrasCount = useMemo(
    () =>
      listAppointmentsData.filter(
        (appointment) => (appointment.extra_services?.length ?? 0) > 0
      ).length,
    [listAppointmentsData]
  );

  const listLateAppointmentsCount = useMemo(() => {
    const nowMs = Date.now();
    return listAppointmentsData.filter((appointment) =>
      isLateActiveAppointment(appointment, nowMs)
    ).length;
  }, [listAppointmentsData]);

  const filteredListAppointments = useMemo(() => {
    const nowMs = Date.now();
    let rows = listAppointmentsData;

    if (listOnlyWithExtras) {
      rows = rows.filter(
        (appointment) => (appointment.extra_services?.length ?? 0) > 0
      );
    }

    if (listLateOnly) {
      rows = rows.filter((appointment) =>
        isLateActiveAppointment(appointment, nowMs)
      );
    }

    return rows;
  }, [listAppointmentsData, listLateOnly, listOnlyWithExtras]);

  const calendarRangeKeyRef = useRef<string | null>(null);
  const gridRef = useRef<AgendaCustomGridHandle>(null);
  const professionalsInitializedRef = useRef(false);
  const unitInitializedRef = useRef(false);
  const deepLinkClientRef = useRef<number | null>(null);
  const deepLinkCreateRef = useRef(false);
  const deepLinkDateInitializedRef = useRef(false);

  const syncAgendaUrl = useCallback(
    (updates: { date?: string | null; resource_page?: number | null }) => {
      setSearchParams(
        (current) => buildAgendaSearchParams(current, updates),
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [units, setUnits] = useState<Unit[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [calendarSlotBounds, setCalendarSlotBounds] = useState(DEFAULT_CALENDAR_BOUNDS);
  const [closedCalendarDates, setClosedCalendarDates] = useState<Set<string>>(new Set());
  const [closedCalendarDayMeta, setClosedCalendarDayMeta] = useState<
    Map<string, ClosedCalendarDayMeta>
  >(new Map());
  const [unitUsesDefaultSchedule, setUnitUsesDefaultSchedule] = useState(false);
  const [loadingCalendarBounds, setLoadingCalendarBounds] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<AppointmentFormMode>("create");
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [formInitialClientId, setFormInitialClientId] = useState<string | undefined>();
  const [createPrefill, setCreatePrefill] = useState<AppointmentCreatePrefill | null>(null);
  const [absenceTarget, setAbsenceTarget] = useState<{
    professional: Professional;
    startLocal: string;
    endLocal: string;
  } | null>(null);

  const [cancelTarget, setCancelTarget] = useState<Appointment | null>(null);
  const [noShowTarget, setNoShowTarget] = useState<Appointment | null>(null);
  const [concludeTarget, setConcludeTarget] = useState<Appointment | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const [showInactiveOnCalendar, setShowInactiveOnCalendar] = useState(() => {
    return getAgendaShowInactiveForTenant(resolveCurrentTenantId()) ?? false;
  });
  const [exitingCalendarEventIds, setExitingCalendarEventIds] = useState<Set<number>>(() => new Set());
  const [pixModalData, setPixModalData] = useState<{
    appointmentId: number;
    qrCodeBase64: string;
    copyPasteString: string;
  } | null>(null);
  const pixPaymentConfirmedRef = useRef(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitingRoomTvOpen, setWaitingRoomTvOpen] = useState(false);
  const [waitingRoomRefreshKey, setWaitingRoomRefreshKey] = useState(0);
  const [waitingRoomBadgeCount, setWaitingRoomBadgeCount] = useState(0);
  const [statusLegendOpen, setStatusLegendOpen] = useState(false);
  const [lastOptionsAction, setLastOptionsAction] = useState<AgendaOptionsActionPreference | null>(
    () => getAgendaLastOptionsActionForTenant(resolveCurrentTenantId())
  );
  const [activeTab, setActiveTab] = useState(() => {
    const stored = getAgendaTabForTenant(resolveCurrentTenantId());

    if (stored === "list" || stored === "waiting") {
      return stored;
    }

    return "calendar";
  });
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(todayIsoDate());
  const [calendarUsesWeekView, setCalendarUsesWeekView] = useState(() => {
    const tenantView = getAgendaCalendarViewForTenant(resolveCurrentTenantId());

    if (tenantView === "week" || tenantView === "day") {
      return tenantView === "week";
    }

    return readAgendaCalendarView() === "week";
  });
  const [resourcePageIndex, setResourcePageIndex] = useState(readAgendaResourcePage);
  const [selectedCalendarAppointment, setSelectedCalendarAppointment] =
    useState<Appointment | null>(null);
  const [anamnesisTarget, setAnamnesisTarget] = useState<{
    id: number;
    name: string;
    serviceId?: number | null;
    initialTemplateId?: number | null;
    evolutionAppointmentId?: number | null;
    initialRecordId?: number | null;
    initialRecordTypeFilter?: "all" | "anamnesis" | "evolution";
  } | null>(null);
  const [batchArriving, setBatchArriving] = useState(false);
  const batchHealthQueueRef = useRef<BatchHealthQueueItem[]>([]);

  const notAvailable = t("studio.agenda.common.notAvailable");
  const canHealthRecord = useMemo(() => canViewClinicalRecords(), []);
  const allowMarkArrived = useMemo(() => canMarkClientArrived(), []);

  useEffect(() => {
    void refreshWaitingRoomBadge();
    if (!filterUnitId) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void refreshWaitingRoomBadge();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [filterUnitId, refreshWaitingRoomBadge, waitingRoomRefreshKey]);

  const lateArrivableAppointments = useMemo(() => {
    if (!allowMarkArrived) {
      return [] as Appointment[];
    }

    const nowMs = Date.now();

    return filteredListAppointments.filter((appointment) =>
      isLateArrivableAppointment(appointment, nowMs)
    );
  }, [allowMarkArrived, filteredListAppointments]);

  const canUseConsolidatedView = useMemo(() => {
    const roles = getAuthRoleNames();

    return roles.some((role) => ADMIN_ROLES.includes(role));
  }, []);

  const allowConclude = useMemo(() => canConcludeAppointments(), []);

  const isConsolidatedView = filterUnitId === "" && canUseConsolidatedView && units.length > 1;

  const resolveUnitName = useCallback(
    (unitId?: number | null): string => {
      if (!unitId) {
        return notAvailable;
      }

      return units.find((unit) => unit.id === unitId)?.name ?? notAvailable;
    },
    [notAvailable, units]
  );

  const loadListAppointments = useCallback(async () => {
    setLoadingList(true);

    try {
      const response = await getAll({
        page,
        per_page: PER_PAGE,
        date: filterDate,
        unit_id: filterUnitId ? Number(filterUnitId) : undefined,
      });

      const sorted = [...response.data].sort((left, right) =>
        left.start_datetime.localeCompare(right.start_datetime)
      );

      setListAppointmentsData(sorted);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("studio.agenda.toast.loadError"));
    } finally {
      setLoadingList(false);
    }
  }, [filterDate, filterUnitId, page, t]);

  const loadCalendarAppointments = useCallback(async () => {
    if (!calendarRange) {
      return;
    }

    setLoadingCalendar(true);

    try {
      const response = await listAppointments({
        per_page: CALENDAR_FETCH_PER_PAGE,
        start_date: calendarRange.start_date,
        end_date: calendarRange.end_date,
        unit_id: filterUnitId ? Number(filterUnitId) : undefined,
      });

      const sorted = [...response.data].sort((left, right) =>
        left.start_datetime.localeCompare(right.start_datetime)
      );

      setCalendarAppointments(sorted);
    } catch (error: any) {
      toast.error(error?.data || t("studio.agenda.toast.loadError"));
    } finally {
      setLoadingCalendar(false);
    }
  }, [calendarRange, filterUnitId, t]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [unitsResponse, professionalsResponse] = await Promise.all([
        getUnits({ per_page: 100 }),
        getProfessionals({ per_page: 100 }),
      ]);

      setUnits(unitsResponse.data);
      setProfessionals(professionalsResponse.data);

      if (!unitInitializedRef.current && unitsResponse.data.length > 0) {
        unitInitializedRef.current = true;

        const tenantId = resolveCurrentTenantId();
        const storedUnit = getAgendaUnitForTenant(tenantId);
        const unitIds = new Set(unitsResponse.data.map((unit) => String(unit.id)));

        if (storedUnit !== null) {
          if (storedUnit === "") {
            const preferConsolidated =
              canUseConsolidatedView && unitsResponse.data.length > 1;
            setFilterUnitId(
              preferConsolidated ? "" : String(unitsResponse.data[0].id)
            );
          } else if (unitIds.has(storedUnit)) {
            setFilterUnitId(storedUnit);
          } else {
            setFilterUnitId(String(unitsResponse.data[0].id));
          }
        } else {
          const preferConsolidated =
            canUseConsolidatedView &&
            unitsResponse.data.length > 1 &&
            readConsolidatedViewPreference();

          setFilterUnitId(
            preferConsolidated ? "" : String(unitsResponse.data[0].id)
          );
        }
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.agenda.toast.loadError"));
    }
  }, [canUseConsolidatedView, t]);

  const loadConsolidatedCalendarContext = useCallback(
    async (unitIds: number[], startDate: string, endDate: string) => {
      if (unitIds.length === 0) {
        return;
      }

      setLoadingCalendarBounds(true);

      try {
        const responses = await Promise.all(
          unitIds.map((unitId) =>
            Promise.all([
              getUnitCalendarBounds(unitId, {
                start_date: startDate,
                end_date: endDate,
              }),
              getUnitSchedules(unitId),
            ])
          )
        );

        const bounds = responses.map(([boundsResponse]) => boundsResponse.data);
        const mergedBounds = mergeCalendarSlotBounds(bounds);

        setCalendarSlotBounds({
          slotMinTime: mergedBounds.slotMinTime,
          slotMaxTime: mergedBounds.slotMaxTime,
          scrollTime: mergedBounds.scrollTime,
        });
        setClosedCalendarDates(mergeClosedCalendarDates(bounds));
        setClosedCalendarDayMeta(mergeClosedCalendarDayMeta(bounds));
        setUnitUsesDefaultSchedule(
          responses.every(([, schedulesResponse]) => schedulesResponse.data.length === 0)
        );
      } catch (error: any) {
        toast.error(error?.data || t("studio.agenda.toast.loadError"));
      } finally {
        setLoadingCalendarBounds(false);
      }
    },
    [t]
  );

  const loadUnitCalendarContext = useCallback(
    async (unitId: number, startDate: string, endDate: string) => {
      setLoadingCalendarBounds(true);

      try {
        const [boundsResponse, schedulesResponse] = await Promise.all([
          getUnitCalendarBounds(unitId, {
            start_date: startDate,
            end_date: endDate,
          }),
          getUnitSchedules(unitId),
        ]);

        setCalendarSlotBounds({
          slotMinTime: boundsResponse.data.slot_min_time,
          slotMaxTime: boundsResponse.data.slot_max_time,
          scrollTime: boundsResponse.data.scroll_time,
        });

        setClosedCalendarDates(
          new Set(
            boundsResponse.data.days.filter((day) => day.closed).map((day) => day.date)
          )
        );
        setClosedCalendarDayMeta(mergeClosedCalendarDayMeta([boundsResponse.data]));
        setUnitUsesDefaultSchedule(schedulesResponse.data.length === 0);
      } catch (error: any) {
        toast.error(error?.data || t("studio.agenda.toast.boundsError"));
        setCalendarSlotBounds(DEFAULT_CALENDAR_BOUNDS);
        setClosedCalendarDates(new Set());
        setClosedCalendarDayMeta(new Map());
        setUnitUsesDefaultSchedule(false);
      } finally {
        setLoadingCalendarBounds(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadListAppointments();
  }, [loadListAppointments]);

  useEffect(() => {
    loadCalendarAppointments();
  }, [loadCalendarAppointments]);

  useEffect(() => {
    if (professionals.length === 0 || professionalsInitializedRef.current) {
      return;
    }

    professionalsInitializedRef.current = true;
    const stored = getAgendaProfessionalIdsForTenant(resolveCurrentTenantId());

    if (stored !== null && stored.length > 0) {
      const validIds = stored.filter((id) => professionals.some((professional) => professional.id === id));
      setSelectedProfessionalIds(validIds.length > 0 ? validIds : professionals.map((professional) => professional.id));
    } else {
      setSelectedProfessionalIds(professionals.map((professional) => professional.id));
    }
  }, [professionals]);

  useEffect(() => {
    if (!professionalsInitializedRef.current) {
      return;
    }

    setAgendaProfessionalIdsForTenant(resolveCurrentTenantId(), selectedProfessionalIds);
  }, [selectedProfessionalIds]);

  useEffect(() => {
    setAgendaShowInactiveForTenant(resolveCurrentTenantId(), showInactiveOnCalendar);
  }, [showInactiveOnCalendar]);

  useEffect(() => {
    setAgendaListOnlyWithExtrasForTenant(
      resolveCurrentTenantId(),
      listOnlyWithExtras
    );
  }, [listOnlyWithExtras]);

  useEffect(() => {
    setAgendaListLateOnlyForTenant(resolveCurrentTenantId(), listLateOnly);
  }, [listLateOnly]);

  useEffect(() => {
    if (!selectedCalendarAppointment) {
      return;
    }

    const fresh = calendarAppointments.find((item) => item.id === selectedCalendarAppointment.id);

    if (fresh && fresh.status !== selectedCalendarAppointment.status) {
      setSelectedCalendarAppointment(fresh);
    }
  }, [calendarAppointments, selectedCalendarAppointment]);

  useEffect(() => {
    if (!selectedCalendarAppointment) {
      return;
    }

    const stillVisible = calendarAppointments.some(
      (item) =>
        item.id === selectedCalendarAppointment.id &&
        selectedProfessionalIds.includes(item.professional_id)
    );

    if (!stillVisible) {
      setSelectedCalendarAppointment(null);
    }
  }, [calendarAppointments, selectedCalendarAppointment, selectedProfessionalIds]);

  useEffect(() => {
    if (activeTab !== "calendar") {
      setSelectedCalendarAppointment(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "calendar" && calendarSelectedDate) {
      setFilterDate(calendarSelectedDate);
    }
  }, [activeTab, calendarSelectedDate]);

  useEffect(() => {
    writeAgendaResourcePage(resourcePageIndex);
  }, [resourcePageIndex]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    if (!calendarRange) {
      return;
    }

    if (isConsolidatedView) {
      void loadConsolidatedCalendarContext(
        units.map((unit) => unit.id),
        calendarRange.start_date,
        calendarRange.end_date
      );
      return;
    }

    if (!filterUnitId) {
      return;
    }

    void loadUnitCalendarContext(
      Number(filterUnitId),
      calendarRange.start_date,
      calendarRange.end_date
    );
  }, [
    calendarRange,
    filterUnitId,
    isConsolidatedView,
    loadConsolidatedCalendarContext,
    loadUnitCalendarContext,
    units,
  ]);

  useEffect(() => {
    if (deepLinkDateInitializedRef.current) {
      return;
    }

    deepLinkDateInitializedRef.current = true;

    const dateParam = parseAgendaDateParam(searchParams.get("date"));
    const pageParam = parseResourcePageParam(searchParams.get("resource_page"));

    if (pageParam !== null) {
      setResourcePageIndex(pageParam);
    }

    if (dateParam) {
      setCalendarSelectedDate(dateParam);
      setFilterDate(dateParam);
      setActiveTab("calendar");
    }
  }, [searchParams]);

  useEffect(() => {
    const clientIdParam = searchParams.get("client_id");

    if (!clientIdParam) {
      deepLinkClientRef.current = null;
      return;
    }

    const parsedClientId = Number(clientIdParam);

    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    if (deepLinkClientRef.current === parsedClientId) {
      return;
    }

    const serviceIdParam = searchParams.get("service_id");
    const unitIdParam = searchParams.get("unit_id");
    const parsedServiceId = serviceIdParam ? Number(serviceIdParam) : NaN;
    const parsedUnitId = unitIdParam ? Number(unitIdParam) : NaN;
    const prefillServiceId =
      Number.isFinite(parsedServiceId) && parsedServiceId > 0
        ? String(parsedServiceId)
        : undefined;
    const prefillUnitId =
      Number.isFinite(parsedUnitId) && parsedUnitId > 0
        ? String(parsedUnitId)
        : undefined;

    deepLinkClientRef.current = parsedClientId;
    setFormMode("create");
    setEditingAppointment(null);
    setFormInitialClientId(String(parsedClientId));
    setCreatePrefill(
      prefillServiceId || prefillUnitId
        ? {
            ...(prefillUnitId ? { unit_id: prefillUnitId } : {}),
            ...(prefillServiceId ? { service_id: prefillServiceId } : {}),
          }
        : null
    );
    setFormOpen(true);
    setSearchParams(
      (current) =>
        buildAgendaSearchParams(current, {
          client_id: null,
          service_id: null,
          unit_id: null,
        }),
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const actionParam = searchParams.get("action");

    if (actionParam !== "create") {
      deepLinkCreateRef.current = false;
      return;
    }

    if (deepLinkCreateRef.current) {
      return;
    }

    deepLinkCreateRef.current = true;
    setFormMode("create");
    setEditingAppointment(null);
    setFormInitialClientId(undefined);
    setCreatePrefill(null);
    setFormOpen(true);
    setSearchParams(
      (current) => buildAgendaSearchParams(current, { action: null }),
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const handleOpenCreateForm = useCallback(() => {
    setFormMode("create");
    setEditingAppointment(null);
    setFormInitialClientId(undefined);
    setCreatePrefill(null);
    setFormOpen(true);
  }, []);

  const handleEditAppointment = useCallback((appointment: Appointment) => {
    setFormMode("edit");
    setEditingAppointment(appointment);
    setFormInitialClientId(undefined);
    setFormOpen(true);
  }, []);

  const handleFormClose = () => {
    setFormOpen(false);
    setFormInitialClientId(undefined);
    setCreatePrefill(null);
    setFormMode("create");
    setEditingAppointment(null);
  };

  const handleAppointmentSaved = async (options?: {
    openAppointmentId?: number;
  }) => {
    await Promise.all([loadListAppointments(), loadCalendarAppointments()]);

    if (!options?.openAppointmentId) {
      return;
    }

    try {
      const response = await getById(options.openAppointmentId);
      if (response.data) {
        setSelectedCalendarAppointment(response.data);
      }
    } catch {
      // list/calendar already refreshed; detail open is best-effort
    }
  };

  const resolveStatusLabel = (status: string): string =>
    t(`studio.agenda.status.${status}`, { defaultValue: status });

  const resolveStatusColor = (status: string): string => {
    switch (status) {
      case "confirmed":
      case "completed":
        return "success";
      case "pending_deposit":
        return "warning";
      case "canceled":
      case "expired":
        return "danger";
      case "no_show":
        return "warning";
      case "scheduled":
        return "primary";
      default:
        return "secondary";
    }
  };

  const isActionableStatus = useCallback(
    (status: string): boolean =>
      (ACTIONABLE_APPOINTMENT_STATUSES as readonly string[]).includes(status),
    []
  );

  const patchLocalAppointment = useCallback(
    (appointmentId: number, next: Appointment | ((current: Appointment) => Appointment)) => {
      const apply = (item: Appointment): Appointment => {
        if (item.id !== appointmentId) {
          return item;
        }

        return typeof next === "function" ? next(item) : next;
      };

      setCalendarAppointments((current) => current.map(apply));
      setListAppointmentsData((current) => current.map(apply));
    },
    []
  );

  const bumpClientHealthRecordsCount = useCallback((clientId: number) => {
    const apply = (item: Appointment): Appointment => {
      if (item.client_id !== clientId || !item.client) {
        return item;
      }

      return {
        ...item,
        needs_health_record: false,
        client: {
          ...item.client,
          health_records_count: (item.client.health_records_count ?? 0) + 1,
        },
      };
    };

    setCalendarAppointments((current) => current.map(apply));
    setListAppointmentsData((current) => current.map(apply));
    setSelectedCalendarAppointment((current) =>
      current ? apply(current) : current
    );
  }, []);

  const openHealthRecordFromQueueItem = useCallback(
    (item: BatchHealthQueueItem) => {
      setAnamnesisTarget({
        id: item.clientId,
        name: item.name,
        serviceId: item.serviceId ?? null,
        initialTemplateId: item.initialTemplateId ?? null,
      });
    },
    []
  );

  const handleAnamnesisRecordSaved = useCallback(
    (clientId: number) => {
      bumpClientHealthRecordsCount(clientId);

      const remaining = batchHealthQueueRef.current.filter(
        (item) => item.clientId !== clientId
      );
      batchHealthQueueRef.current = remaining;

      if (remaining.length === 0) {
        return;
      }

      void (async () => {
        const next = remaining[0];
        const decision = await promptContinueBatchHealthQueue(
          next,
          remaining.length,
          t
        );

        if (decision !== "fill") {
          batchHealthQueueRef.current = [];
          return;
        }

        batchHealthQueueRef.current = remaining.slice(1);
        openHealthRecordFromQueueItem(next);
      })();
    },
    [bumpClientHealthRecordsCount, openHealthRecordFromQueueItem, t]
  );

  const openHealthRecordForAppointment = useCallback((appointment: Appointment) => {
    if (!appointment.client_id) {
      return;
    }

    setAnamnesisTarget({
      id: appointment.client_id,
      name: appointment.client?.name ?? notAvailable,
      serviceId: appointment.service_id,
      initialTemplateId: appointment.service?.client_record_template_id ?? null,
    });
  }, [notAvailable]);

  const openRegisterEvolutionForAppointment = useCallback(
    (appointment: Appointment) => {
      if (!appointment.client_id) {
        return;
      }

      setAnamnesisTarget({
        id: appointment.client_id,
        name: appointment.client?.name ?? notAvailable,
        serviceId: appointment.service_id,
        evolutionAppointmentId: appointment.id,
        initialRecordTypeFilter: "evolution",
      });
    },
    [notAvailable]
  );

  const openViewEvolutionForAppointment = useCallback(
    (appointment: Appointment) => {
      if (!appointment.client_id) {
        return;
      }

      setAnamnesisTarget({
        id: appointment.client_id,
        name: appointment.client?.name ?? notAvailable,
        initialRecordId: appointment.evolution_record_id ?? null,
        initialRecordTypeFilter: "evolution",
      });
    },
    [notAvailable]
  );

  const promptFillHealthRecordAfterArrived = useCallback(
    async (appointment: Appointment) => {
      await promptFillHealthRecordIfMissing({
        canView: canHealthRecord,
        appointment,
        notAvailable,
        t,
        onFill: openHealthRecordForAppointment,
      });
    },
    [canHealthRecord, notAvailable, openHealthRecordForAppointment, t]
  );

  const requestConcludeAppointment = useCallback(
    async (appointment: Appointment) => {
      // Check clinical gate first if present
      if (appointment.has_clinical_gate_alert) {
        const mayProceed = await confirmClinicalGateContinue({
          t,
          alerts: appointment.clinical_gate_alerts || [],
          onOpenRecord: canHealthRecord ? () => openHealthRecordForAppointment(appointment) : undefined,
        });

        if (!mayProceed) {
          return;
        }
      }

      // Then check preattend gap
      const mayProceed = await confirmConcludeDespitePreattendGap({
        canView: canHealthRecord,
        appointment,
        t,
        onFill: openHealthRecordForAppointment,
      });

      if (!mayProceed) {
        return;
      }

      setConcludeTarget(appointment);
    },
    [canHealthRecord, openHealthRecordForAppointment, t]
  );

  const handleClearArrived = useCallback(
    async (appointment: Appointment) => {
      setStatusUpdatingId(appointment.id);
      const previous = appointment;

      try {
        const response = await clearClientArrived(appointment.id);
        const next = response.data ?? { ...appointment, client_arrived_at: null };
        patchLocalAppointment(appointment.id, next);
        setSelectedCalendarAppointment((current) =>
          current?.id === appointment.id ? next : current
        );
        toast.info(response.message || t("studio.agenda.actions.arrived.undone"));
      } catch (error: unknown) {
        patchLocalAppointment(appointment.id, previous);
        setSelectedCalendarAppointment((current) =>
          current?.id === appointment.id ? previous : current
        );
        const apiError = error as { data?: string };
        toast.error(apiError?.data || t("studio.agenda.actions.arrived.undoError"));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [patchLocalAppointment, t]
  );

  const handleMarkArrived = useCallback(
    async (appointment: Appointment) => {
      if (
        !allowMarkArrived ||
        appointment.client_arrived_at ||
        !["scheduled", "confirmed"].includes(appointment.status)
      ) {
        return;
      }

      setStatusUpdatingId(appointment.id);
      const previous = appointment;

      try {
        const response = await markClientArrived(appointment.id);
        const next =
          response.data ??
          ({
            ...appointment,
            client_arrived_at: new Date().toISOString(),
          } as Appointment);

        patchLocalAppointment(appointment.id, next);
        setSelectedCalendarAppointment((current) =>
          current?.id === appointment.id ? next : current
        );

        playArrivedChime();
        setWaitingRoomRefreshKey((value) => value + 1);
        void refreshWaitingRoomBadge();

        toast.success(
          <span>
            {response.message || t("studio.agenda.actions.arrived.success")}{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => {
                void handleClearArrived(next);
              }}
            >
              {t("studio.agenda.actions.arrived.undo")}
            </button>
          </span>,
          { closeOnClick: false, autoClose: 8000 }
        );

        // Show clinical gate modal BEFORE health record prompt if in warn mode
        if (next.has_clinical_gate_alert) {
          const mayProceed = await confirmClinicalGateContinue({
            t,
            alerts: next.clinical_gate_alerts || [],
            onOpenRecord: canHealthRecord ? () => openHealthRecordForAppointment(next) : undefined,
          });

          if (!mayProceed) {
            return;
          }
        }

        void promptFillHealthRecordAfterArrived(next);
      } catch (error: unknown) {
        // Check if CLINICAL_GATE_ACK_REQUIRED
        if (isClinicalGateAckRequired(error)) {
          const alerts = extractClinicalGateAlertsFromError(error);
          const mayProceed = await confirmClinicalGateContinue({
            t,
            alerts,
            onOpenRecord: canHealthRecord ? () => openHealthRecordForAppointment(appointment) : undefined,
          });

          if (mayProceed) {
            // Retry with acknowledgment
            try {
              const retryResponse = await markClientArrived(appointment.id, { acknowledge_clinical_gate: true });
              const next =
                retryResponse.data ??
                ({
                  ...appointment,
                  client_arrived_at: new Date().toISOString(),
                } as Appointment);

              patchLocalAppointment(appointment.id, next);
              setSelectedCalendarAppointment((current) =>
                current?.id === appointment.id ? next : current
              );

              playArrivedChime();
              setWaitingRoomRefreshKey((value) => value + 1);
              void refreshWaitingRoomBadge();

              toast.success(
                <span>
                  {retryResponse.message || t("studio.agenda.actions.arrived.success")}{" "}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 align-baseline"
                    onClick={() => {
                      void handleClearArrived(next);
                    }}
                  >
                    {t("studio.agenda.actions.arrived.undo")}
                  </button>
                </span>,
                { closeOnClick: false, autoClose: 8000 }
              );

              void promptFillHealthRecordAfterArrived(next);
              return;
            } catch (retryError: unknown) {
              patchLocalAppointment(appointment.id, previous);
              setSelectedCalendarAppointment((current) =>
                current?.id === appointment.id ? previous : current
              );
              const apiError = retryError as { data?: string };
              toast.error(apiError?.data || t("studio.agenda.actions.arrived.error"));
              setStatusUpdatingId(null);
              return;
            }
          } else {
            patchLocalAppointment(appointment.id, previous);
            setSelectedCalendarAppointment((current) =>
              current?.id === appointment.id ? previous : current
            );
            setStatusUpdatingId(null);
            return;
          }
        }

        patchLocalAppointment(appointment.id, previous);
        setSelectedCalendarAppointment((current) =>
          current?.id === appointment.id ? previous : current
        );
        const apiError = error as { data?: string };
        toast.error(apiError?.data || t("studio.agenda.actions.arrived.error"));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [
      allowMarkArrived,
      canHealthRecord,
      handleClearArrived,
      openHealthRecordForAppointment,
      patchLocalAppointment,
      promptFillHealthRecordAfterArrived,
      refreshWaitingRoomBadge,
      t,
    ]
  );

  const handleBatchMarkArrived = useCallback(async () => {
    if (!allowMarkArrived || lateArrivableAppointments.length === 0 || batchArriving) {
      return;
    }

    setBatchArriving(true);
    let ok = 0;
    const markedWithoutHealth: Appointment[] = [];

    try {
      for (const appointment of lateArrivableAppointments) {
        try {
          const response = await markClientArrived(appointment.id);
          const next =
            response.data ??
            ({
              ...appointment,
              client_arrived_at: new Date().toISOString(),
            } as Appointment);
          patchLocalAppointment(appointment.id, next);
          setSelectedCalendarAppointment((current) =>
            current?.id === appointment.id ? next : current
          );
          ok += 1;

          if (appointmentNeedsHealthRecord(next) && next.client_id) {
            markedWithoutHealth.push(next);
          }
        } catch {
          // continue others
        }
      }

      if (ok > 0) {
        playArrivedChime();
        setWaitingRoomRefreshKey((value) => value + 1);
        void refreshWaitingRoomBadge();
        toast.success(
          ok === 1
            ? t("studio.agenda.actions.arrived.batchSuccessOne")
            : t("studio.agenda.actions.arrived.batchSuccess", { count: ok })
        );

        if (canHealthRecord && markedWithoutHealth.length > 0) {
          const confirmation = await Swal.fire({
            title: t("studio.agenda.actions.arrived.batchMissingHealthTitle"),
            text: t("studio.agenda.actions.arrived.batchMissingHealthBody", {
              count: markedWithoutHealth.length,
            }),
            icon: "question",
            showCancelButton: true,
            confirmButtonText: t(
              "studio.agenda.actions.arrived.batchMissingHealthFill"
            ),
            cancelButtonText: t(
              "studio.agenda.actions.arrived.batchMissingHealthLater"
            ),
            confirmButtonColor: "#0ab39c",
            cancelButtonColor: "#878a99",
            reverseButtons: true,
          });

          if (confirmation.isConfirmed) {
            const queue = markedWithoutHealth
              .map((item) => appointmentToBatchHealthQueueItem(item, notAvailable))
              .filter((item): item is BatchHealthQueueItem => item !== null);
            if (queue.length > 0) {
              batchHealthQueueRef.current = queue.slice(1);
              openHealthRecordFromQueueItem(queue[0]);
            }
          } else {
            batchHealthQueueRef.current = [];
          }
        }
      } else {
        toast.error(t("studio.agenda.actions.arrived.batchError"));
      }
    } finally {
      setBatchArriving(false);
    }
  }, [
    allowMarkArrived,
    batchArriving,
    canHealthRecord,
    lateArrivableAppointments,
    notAvailable,
    openHealthRecordFromQueueItem,
    patchLocalAppointment,
    refreshWaitingRoomBadge,
    t,
  ]);

  const handleClearArrivedFromGrid = useCallback(
    async (appointment: Appointment) => {
      if (!allowMarkArrived || !appointment.client_arrived_at) {
        return;
      }

      const confirmation = await Swal.fire({
        title: t("studio.agenda.actions.arrived.clearConfirmTitle"),
        text: t("studio.agenda.actions.arrived.clearConfirm", {
          name: appointment.client?.name ?? notAvailable,
        }),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("studio.agenda.actions.arrived.clearConfirmButton"),
        cancelButtonText: t("studio.agenda.actions.cancel"),
        confirmButtonColor: "#f06548",
        cancelButtonColor: "#878a99",
        reverseButtons: true,
      });

      if (!confirmation.isConfirmed) {
        return;
      }

      await handleClearArrived(appointment);
    },
    [allowMarkArrived, handleClearArrived, notAvailable, t]
  );

  const markCalendarEventExiting = useCallback((appointmentId: number) => {
    setExitingCalendarEventIds((current) => new Set(current).add(appointmentId));

    window.setTimeout(() => {
      setExitingCalendarEventIds((current) => {
        const next = new Set(current);
        next.delete(appointmentId);

        return next;
      });
    }, CALENDAR_EXIT_ANIMATION_MS);
  }, []);

  const applyAppointmentStatusChange = useCallback(
    async (
      appointment: Appointment,
      newStatus: string,
      options: {
        successMessageKey: string;
        errorMessageKey: string;
        keepPanelOpen?: boolean;
        animateExit?: boolean;
      }
    ) => {
      const appointmentId = appointment.id;
      const previousAppointment = appointment;

      setStatusUpdatingId(appointmentId);

      const shouldAnimateExit =
        options.animateExit !== false &&
        !showInactiveOnCalendar &&
        isInactiveCalendarStatus(newStatus);

      if (shouldAnimateExit) {
        markCalendarEventExiting(appointmentId);
      }

      patchLocalAppointment(appointmentId, (item) => ({ ...item, status: newStatus }));

      if (options.keepPanelOpen) {
        setSelectedCalendarAppointment((current) =>
          current?.id === appointmentId ? { ...current, status: newStatus } : current
        );
      }

      try {
        const response = await updateStatus(appointmentId, { status: newStatus });
        toast.success(response.message || t(options.successMessageKey));

        if (response.data) {
          patchLocalAppointment(appointmentId, response.data);

          if (options.keepPanelOpen) {
            setSelectedCalendarAppointment(response.data);
          }
        }

        void Promise.all([loadListAppointments(), loadCalendarAppointments()]);
      } catch (error: any) {
        patchLocalAppointment(appointmentId, previousAppointment);

        if (options.keepPanelOpen) {
          setSelectedCalendarAppointment(previousAppointment);
        }

        toast.error(error?.data || t(options.errorMessageKey));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [
      loadCalendarAppointments,
      loadListAppointments,
      markCalendarEventExiting,
      patchLocalAppointment,
      showInactiveOnCalendar,
      t,
    ]
  );

  const handleCancelAppointment = async () => {
    if (!cancelTarget) {
      return;
    }

    const target = cancelTarget;
    setCancelTarget(null);

    await applyAppointmentStatusChange(target, "canceled", {
      successMessageKey: "studio.agenda.actions.cancelAppointment.success",
      errorMessageKey: "studio.agenda.actions.cancelAppointment.error",
      keepPanelOpen: true,
    });
  };

  const handleNoShowAppointment = async () => {
    if (!noShowTarget) {
      return;
    }

    const target = noShowTarget;
    setNoShowTarget(null);

    await applyAppointmentStatusChange(target, "no_show", {
      successMessageKey: "studio.agenda.actions.noShow.success",
      errorMessageKey: "studio.agenda.actions.noShow.error",
      keepPanelOpen: true,
    });
  };

  const handleConfirmManualDeposit = useCallback(
    async (appointment: Appointment) => {
      const confirmation = await Swal.fire({
        title: t("studio.agenda.deposit.confirmation.confirmReceived"),
        text: t("studio.agenda.deposit.confirmation.confirmPrompt"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("studio.agenda.deposit.confirmation.confirmButton"),
        cancelButtonText: t("studio.agenda.deposit.confirmation.confirmDismiss"),
        confirmButtonColor: "#0ab39c",
        cancelButtonColor: "#878a99",
      });

      if (!confirmation.isConfirmed) {
        return;
      }

      setStatusUpdatingId(appointment.id);

      try {
        const response = await confirmDeposit(appointment.id);
        toast.success(response.message || t("studio.agenda.deposit.confirmation.success"));
        setSelectedCalendarAppointment(response.data);
        await Promise.all([loadListAppointments(), loadCalendarAppointments()]);
      } catch (error: any) {
        toast.error(error?.data || t("studio.agenda.deposit.confirmation.error"));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [loadCalendarAppointments, loadListAppointments, t]
  );

  const handleConcludeAppointment = async () => {
    if (!concludeTarget) {
      return;
    }

    setStatusUpdatingId(concludeTarget.id);

    try {
      const response = await update(concludeTarget.id, {
        status: "completed",
        ...(concludeTarget.has_clinical_gate_alert
          ? { acknowledge_clinical_gate: true }
          : {}),
      });

      if (response.order_id) {
        toast.success(t("studio.agenda.actions.completing_toast"));
        setConcludeTarget(null);
        setSelectedCalendarAppointment(null);
        navigate(`/orders/${response.order_id}`);
        return;
      }

      toast.success(response.message || t("studio.agenda.actions.conclude.success"));
      setConcludeTarget(null);
      setSelectedCalendarAppointment(null);
      await Promise.all([loadListAppointments(), loadCalendarAppointments()]);
    } catch (error: any) {
      toast.error(error?.data || t("studio.agenda.actions.conclude.error"));
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const paginationFrom = meta?.from ?? 0;
  const paginationTo = meta?.to ?? 0;
  const paginationTotal = meta?.total ?? 0;
  const paginationLastPage = meta?.last_page ?? 1;

  const resolveProfessionalColor = useCallback(
    (appointment: Appointment): string => {
      if (appointment.professional?.color_hex) {
        return appointment.professional.color_hex;
      }

      const professional = professionals.find((item) => item.id === appointment.professional_id);

      return professional?.color_hex ?? DEFAULT_EVENT_COLOR;
    },
    [professionals]
  );

  const formatTimeRange = useCallback(
    (start: string, end: string) =>
      t("studio.agenda.fields.slot.option", {
        start: formatWallClockTime(start),
        end: formatWallClockTime(end),
      }),
    [t]
  );

  const openAppointmentDetail = useCallback(async (appointmentId: number) => {
    try {
      const response = await getById(appointmentId);
      if (response.data) {
        setSelectedCalendarAppointment(response.data);
      }
    } catch {
      // best-effort
    }
  }, []);

  const handlePixPaymentConfirmed = useCallback(async () => {
    const appointmentId = pixModalData?.appointmentId ?? null;
    pixPaymentConfirmedRef.current = true;
    window.dispatchEvent(new CustomEvent("adatis:notifications:refresh"));
    await Promise.all([loadListAppointments(), loadCalendarAppointments()]);

    if (appointmentId != null) {
      await openAppointmentDetail(appointmentId);
    }

    setPixModalData(null);
  }, [
    loadCalendarAppointments,
    loadListAppointments,
    openAppointmentDetail,
    pixModalData?.appointmentId,
  ]);

  const handlePixModalClose = useCallback(() => {
    const appointmentId = pixModalData?.appointmentId ?? null;
    const wasConfirmed = pixPaymentConfirmedRef.current;
    pixPaymentConfirmedRef.current = false;
    setPixModalData(null);

    if (appointmentId == null || wasConfirmed) {
      return;
    }

    toast.info(
      ({ closeToast }) => (
        <div className="d-flex flex-column gap-1">
          <span>{t("agenda.pix.closedPendingToast")}</span>
          <button
            type="button"
            className="btn btn-sm btn-soft-primary align-self-start px-2 py-1"
            onClick={(event) => {
              event.stopPropagation();
              void openAppointmentDetail(appointmentId);
              closeToast?.();
            }}
          >
            {t("agenda.pix.viewAppointment")}
          </button>
        </div>
      ),
      { autoClose: 8000 }
    );
  }, [openAppointmentDetail, pixModalData?.appointmentId, t]);

  const isVisibleOnCalendar = useCallback(
    (appointment: Appointment): boolean => {
      if (!selectedProfessionalIds.includes(appointment.professional_id)) {
        return false;
      }

      const inactive = isInactiveCalendarStatus(appointment.status);

      if (showInactiveOnCalendar) {
        return true;
      }

      if (inactive && exitingCalendarEventIds.has(appointment.id)) {
        return true;
      }

      return !inactive;
    },
    [exitingCalendarEventIds, selectedProfessionalIds, showInactiveOnCalendar]
  );

  const selectedProfessionals = useMemo(
    () =>
      professionals
        .filter((professional) => selectedProfessionalIds.includes(professional.id))
        .sort((left, right) => left.name.localeCompare(right.name, i18n.language)),
    [i18n.language, professionals, selectedProfessionalIds]
  );

  const useResourceColumns =
    !isConsolidatedView && !calendarUsesWeekView && selectedProfessionals.length > 1;

  const resourcePageCount = useMemo(() => {
    if (selectedProfessionals.length <= AGENDA_RESOURCE_PAGE_SIZE) {
      return 1;
    }

    return Math.ceil(selectedProfessionals.length / AGENDA_RESOURCE_PAGE_SIZE);
  }, [selectedProfessionals.length]);

  const clampedResourcePageIndex = useMemo(
    () => clampResourcePage(resourcePageIndex, selectedProfessionals.length, AGENDA_RESOURCE_PAGE_SIZE),
    [resourcePageIndex, selectedProfessionals.length]
  );

  useEffect(() => {
    if (clampedResourcePageIndex !== resourcePageIndex) {
      setResourcePageIndex(clampedResourcePageIndex);
    }
  }, [clampedResourcePageIndex, resourcePageIndex]);

  const pagedProfessionals = useMemo(
    () => paginateIds(selectedProfessionals, clampedResourcePageIndex, AGENDA_RESOURCE_PAGE_SIZE),
    [clampedResourcePageIndex, selectedProfessionals]
  );

  const visibleResourceIds = useMemo(
    () => new Set(pagedProfessionals.map((professional) => String(professional.id))),
    [pagedProfessionals]
  );

  useEffect(() => {
    if (activeTab !== "calendar") {
      return;
    }

    const weekDates = buildWeekDayDates(new Date(`${calendarSelectedDate}T12:00:00`));
    const start_date = calendarUsesWeekView
      ? formatIsoDate(weekDates[0])
      : calendarSelectedDate;
    const end_date = calendarUsesWeekView
      ? formatIsoDate(weekDates[6])
      : calendarSelectedDate;
    const rangeKey = `${start_date}|${end_date}|${filterUnitId}`;

    if (calendarRangeKeyRef.current === rangeKey) {
      return;
    }

    calendarRangeKeyRef.current = rangeKey;
    setCalendarRange({ start_date, end_date });
  }, [activeTab, calendarSelectedDate, calendarUsesWeekView, filterUnitId]);

  const gridColumns = useMemo((): AgendaGridColumn[] => {
    const today = todayIsoDate();

    if (calendarUsesWeekView) {
      return buildWeekDayDates(new Date(`${calendarSelectedDate}T12:00:00`)).map((date) => {
        const iso = formatIsoDate(date);

        return {
          id: iso,
          title: new Intl.DateTimeFormat(i18n.language, { weekday: "short" }).format(date),
          subtitle: new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short" }).format(
            date
          ),
          isClosed: closedCalendarDates.has(iso),
          isToday: iso === today,
        };
      });
    }

    const dayMeta = {
      isClosed: closedCalendarDates.has(calendarSelectedDate),
      isToday: calendarSelectedDate === today,
    };

    if (useResourceColumns) {
      return pagedProfessionals.map((professional) => ({
        id: String(professional.id),
        title: professional.name,
        accentColor: professional.color_hex ?? DEFAULT_EVENT_COLOR,
        ...dayMeta,
      }));
    }

    if (selectedProfessionals.length === 1) {
      const professional = selectedProfessionals[0];

      return [
        {
          id: String(professional.id),
          title: professional.name,
          accentColor: professional.color_hex ?? DEFAULT_EVENT_COLOR,
          ...dayMeta,
        },
      ];
    }

    return [
      {
        id: "all",
        title: isConsolidatedView
          ? t("studio.agenda.calendar.consolidatedColumn")
          : t("studio.agenda.calendar.allColumn"),
        ...dayMeta,
      },
    ];
  }, [
    calendarSelectedDate,
    calendarUsesWeekView,
    closedCalendarDates,
    i18n.language,
    isConsolidatedView,
    pagedProfessionals,
    selectedProfessionals,
    t,
    useResourceColumns,
  ]);

  const visibleDayAppointmentCount = useMemo(
    () =>
      calendarAppointments.filter((appointment) => {
        if (appointment.start_datetime.slice(0, 10) !== calendarSelectedDate) {
          return false;
        }

        if (useResourceColumns && !visibleResourceIds.has(String(appointment.professional_id))) {
          return false;
        }

        return isVisibleOnCalendar(appointment);
      }).length,
    [
      calendarAppointments,
      calendarSelectedDate,
      isVisibleOnCalendar,
      useResourceColumns,
      visibleResourceIds,
    ]
  );

  const agendaGridBlocks = useMemo(
    (): AgendaGridBlock[] => {
      const visibleColumnDates = calendarUsesWeekView
        ? new Set(gridColumns.map((column) => column.id))
        : new Set([calendarSelectedDate]);

      return calendarAppointments
        .filter(isVisibleOnCalendar)
        .filter((appointment) => visibleColumnDates.has(appointment.start_datetime.slice(0, 10)))
        .filter(
          (appointment) =>
            !useResourceColumns || visibleResourceIds.has(String(appointment.professional_id))
        )
        .flatMap((appointment) => {
          const clientName = appointment.client?.name ?? notAvailable;
          const extrasCount = appointment.extra_services?.length ?? 0;
          const serviceName =
            (appointment.service?.name ?? notAvailable) +
            (extrasCount > 0 ? ` (+${extrasCount})` : "");
          const eventColor = resolveProfessionalColor(appointment);
          const isBundle = isBundleAppointment(appointment);
          const isPendingDeposit = appointment.status === "pending_deposit";
          const isInactive = isInactiveCalendarStatus(appointment.status);
          const isExiting = exitingCalendarEventIds.has(appointment.id);
          const isNoShow = appointment.status === "no_show";
          const isDraggable =
            isActionableStatus(appointment.status) && !isBundle && !isInactive && !isExiting;
          const bundlePrefix = isBundle ? `[${t("studio.agenda.calendar.bundleBadge")}] ` : "";
          const depositPrefix = isPendingDeposit
            ? `[${t("studio.agenda.calendar.pendingDepositBadge")}] `
            : "";
          const unitPrefix = isConsolidatedView
            ? `[${resolveUnitName(appointment.unit_id)}] `
            : "";
          const classNames = [
            ...(isBundle ? ["agenda-event-bundle"] : []),
            ...(extrasCount > 0 && !isBundle ? ["agenda-event-has-extras"] : []),
            ...(isPendingDeposit ? ["agenda-event-pending-deposit"] : []),
            ...(showInactiveOnCalendar && isInactive && !isExiting
              ? [isNoShow ? "agenda-event-no-show" : "agenda-event-canceled"]
              : []),
            ...(isExiting ? ["agenda-event-exiting"] : []),
          ];

          const blocks: AgendaGridBlock[] = [
            {
              id: String(appointment.id),
              kind: "appointment",
              appointment,
              title: `${unitPrefix}${depositPrefix}${bundlePrefix}${clientName} — ${serviceName}`,
              startDatetime: appointment.start_datetime,
              endDatetime: appointment.end_datetime,
              color: eventColor,
              borderColor: isBundle ? "#ffffff" : eventColor,
              classNames,
              draggable: isDraggable,
              professionalId: appointment.professional_id,
              date: appointment.start_datetime.slice(0, 10),
            },
          ];

          const bufferMinutes = appointment.service?.buffer_minutes ?? 0;

          if (bufferMinutes > 0 && !isInactive && !isExiting) {
            blocks.push({
              id: `buffer-${appointment.id}`,
              kind: "buffer",
              appointment,
              title: t("studio.agenda.calendar.bufferTitle", { minutes: bufferMinutes }),
              startDatetime: appointment.end_datetime,
              endDatetime: addWallClockMinutes(appointment.end_datetime, bufferMinutes).replace(
                "T",
                " "
              ),
              color: eventColor,
              borderColor: eventColor,
              classNames: ["agenda-event-buffer"],
              draggable: false,
              professionalId: appointment.professional_id,
              date: appointment.start_datetime.slice(0, 10),
            });
          }

          return blocks;
        });
    },
    [
      calendarAppointments,
      calendarSelectedDate,
      calendarUsesWeekView,
      exitingCalendarEventIds,
      gridColumns,
      isConsolidatedView,
      isVisibleOnCalendar,
      notAvailable,
      resolveProfessionalColor,
      resolveUnitName,
      showInactiveOnCalendar,
      t,
      useResourceColumns,
      visibleResourceIds,
      isActionableStatus,
    ]
  );

  const handleCalendarEventClick = useCallback((appointment: Appointment) => {
    setSelectedCalendarAppointment(appointment);
  }, []);

  const navigateCalendarToDate = useCallback(
    (isoDate: string) => {
      setCalendarSelectedDate(isoDate);
      setFilterDate(isoDate);
      setPage(1);
      syncAgendaUrl({ date: isoDate });
    },
    [syncAgendaUrl]
  );

  const persistCalendarView = useCallback((weekMode: boolean) => {
    const view = weekMode ? "week" : "day";
    writeAgendaCalendarView(view);
    setAgendaCalendarViewForTenant(resolveCurrentTenantId(), view);
  }, []);

  const handleWeekStripSelect = useCallback(
    (isoDate: string) => {
      if (calendarUsesWeekView) {
        setCalendarUsesWeekView(false);
        persistCalendarView(false);
      }

      navigateCalendarToDate(isoDate);
    },
    [calendarUsesWeekView, navigateCalendarToDate, persistCalendarView]
  );

  const handleSelectWeekView = useCallback(() => {
    setCalendarUsesWeekView(true);
    persistCalendarView(true);

    if (activeTab !== "calendar") {
      setActiveTab("calendar");
      setAgendaTabForTenant(resolveCurrentTenantId(), "calendar");
    }
  }, [activeTab, persistCalendarView]);

  const handleOpenStatusLegend = useCallback(() => {
    if (activeTab !== "calendar") {
      setActiveTab("calendar");
      setAgendaTabForTenant(resolveCurrentTenantId(), "calendar");
    }

    setStatusLegendOpen(true);
  }, [activeTab]);

  const handleRememberOptionsAction = useCallback((action: AgendaOptionsActionPreference) => {
    setLastOptionsAction(action);
    setAgendaLastOptionsActionForTenant(resolveCurrentTenantId(), action);
  }, []);

  const handleScrollToNow = useCallback(() => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    gridRef.current?.scrollToTime(`${hours}:${minutes}:00`);
  }, []);

  const handlePrintDay = useCallback(() => {
    window.print();
  }, []);

  const handleCalendarPrev = useCallback(() => {
    const delta = calendarUsesWeekView ? -7 : -1;
    navigateCalendarToDate(addDaysToIsoDate(calendarSelectedDate, delta));
  }, [calendarSelectedDate, calendarUsesWeekView, navigateCalendarToDate]);

  const handleCalendarNext = useCallback(() => {
    const delta = calendarUsesWeekView ? 7 : 1;
    navigateCalendarToDate(addDaysToIsoDate(calendarSelectedDate, delta));
  }, [calendarSelectedDate, calendarUsesWeekView, navigateCalendarToDate]);

  const handleCalendarToday = useCallback(() => {
    navigateCalendarToDate(todayIsoDate());
  }, [navigateCalendarToDate]);

  const handleWeekColumnHeaderClick = useCallback(
    (columnId: string) => {
      setCalendarUsesWeekView(false);
      persistCalendarView(false);
      navigateCalendarToDate(columnId);
    },
    [navigateCalendarToDate, persistCalendarView]
  );

  useEffect(() => {
    if (activeTab !== "calendar") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();

      if (
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        target?.isContentEditable
      ) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        handleCalendarPrev();
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        handleCalendarNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeTab, handleCalendarNext, handleCalendarPrev]);

  useEffect(() => {
    if (activeTab !== "calendar" || calendarUsesWeekView) {
      return;
    }

    if (calendarSelectedDate !== todayIsoDate()) {
      return;
    }

    handleScrollToNow();
  }, [activeTab, calendarSelectedDate, calendarUsesWeekView, handleScrollToNow]);

  const handleGridSlotClick = useCallback(
    (params: { date: string; columnId: string; minutesFromMidnight: number }) => {
      const snappedMinutes = snapMinutesToSlot(params.minutesFromMidnight);
      const hours = Math.floor(snappedMinutes / 60);
      const mins = snappedMinutes % 60;
      const preferredStart = `${params.date} ${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;

      setFormMode("create");
      setEditingAppointment(null);
      setFormInitialClientId(undefined);
      setCreatePrefill({
        unit_id: filterUnitId || undefined,
        professional_id:
          !calendarUsesWeekView && params.columnId !== "all" ? params.columnId : undefined,
        date: params.date,
        preferred_start: preferredStart,
      });
      setFormOpen(true);
    },
    [calendarUsesWeekView, filterUnitId]
  );

  const handleGridEventDrop = useCallback(
    async (params: {
      appointment: Appointment;
      newColumnId: string;
      newStartDatetime: string;
    }) => {
      const { appointment, newColumnId, newStartDatetime } = params;

      if (!isActionableStatus(appointment.status) || isBundleAppointment(appointment)) {
        toast.info(t("studio.agenda.calendar.dragNotAllowed"));
        return;
      }

      const newProfessionalId =
        !calendarUsesWeekView && newColumnId !== "all"
          ? Number(newColumnId)
          : appointment.professional_id;

      if (!Number.isFinite(newProfessionalId) || newProfessionalId <= 0) {
        return;
      }

      const professionalChanged = newProfessionalId !== appointment.professional_id;

      if (professionalChanged) {
        const confirmation = await Swal.fire({
          title: t("studio.agenda.actions.edit.professionalChangeTitle"),
          text: t("studio.agenda.actions.edit.professionalChangeConfirm"),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: t("studio.agenda.actions.edit.professionalChangeConfirmButton"),
          cancelButtonText: t("studio.agenda.actions.edit.professionalChangeDismiss"),
          confirmButtonColor: "#0ab39c",
          cancelButtonColor: "#878a99",
        });

        if (!confirmation.isConfirmed) {
          return;
        }
      }

      setStatusUpdatingId(appointment.id);

      try {
        const response = await update(appointment.id, {
          start_datetime: newStartDatetime,
          ...(professionalChanged ? { professional_id: newProfessionalId } : {}),
        });

        toast.success(
          response.message ||
            (professionalChanged
              ? t("studio.agenda.calendar.dragRescheduleSuccessProfessional")
              : t("studio.agenda.calendar.dragRescheduleSuccess"))
        );

        if (selectedCalendarAppointment?.id === appointment.id && response.data) {
          setSelectedCalendarAppointment(response.data);
        }

        await Promise.all([loadListAppointments(), loadCalendarAppointments()]);
      } catch (error: any) {
        toast.error(error?.data || t("studio.agenda.calendar.dragRescheduleError"));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [
      calendarUsesWeekView,
      isActionableStatus,
      loadCalendarAppointments,
      loadListAppointments,
      selectedCalendarAppointment?.id,
      t,
    ]
  );

  const handleWeekStripNavigate = useCallback(
    (direction: "prev" | "next") => {
      const delta = direction === "prev" ? -7 : 7;
      navigateCalendarToDate(addDaysToIsoDate(calendarSelectedDate, delta));
    },
    [calendarSelectedDate, navigateCalendarToDate]
  );

  const handleResourcePageChange = useCallback(
    (nextPage: number) => {
      setResourcePageIndex(nextPage);
      syncAgendaUrl({ resource_page: nextPage });
    },
    [syncAgendaUrl]
  );

  const toggleProfessionalSelection = (professionalId: number) => {
    setSelectedProfessionalIds((current) => {
      if (current.includes(professionalId)) {
        if (current.length <= 1) {
          return current;
        }

        return current.filter((id) => id !== professionalId);
      }

      return [...current, professionalId];
    });
  };

  const selectAllProfessionals = () => {
    setSelectedProfessionalIds(professionals.map((professional) => professional.id));
  };

  const selectNoProfessionals = () => {
    const firstId = professionals[0]?.id;

    setSelectedProfessionalIds(firstId ? [firstId] : []);
  };

  const unitScheduleHelpUrl = useMemo(
    () => getHelpArticleUrl(HELP_ARTICLE_UNIT_SCHEDULE, i18n.language),
    [i18n.language]
  );

  const agendaGuideUrl = useMemo(
    () => getHelpArticleUrl(HELP_ARTICLE_AGENDA, i18n.language),
    [i18n.language]
  );

  const chromeDaySummary = useMemo(() => {
    const date = new Date(`${calendarSelectedDate}T12:00:00`);
    const weekdayRaw = new Intl.DateTimeFormat(i18n.language, { weekday: "short" }).format(date);
    const weekday =
      weekdayRaw.charAt(0).toLocaleUpperCase(i18n.language) + weekdayRaw.slice(1);
    const dayMonth = new Intl.DateTimeFormat(i18n.language, {
      day: "2-digit",
      month: "2-digit",
    }).format(date);
    const countLabel = t("studio.agenda.calendar.dayCount", {
      count: visibleDayAppointmentCount,
    });
    const hasConfiguredHours =
      !unitUsesDefaultSchedule && (isConsolidatedView || Boolean(filterUnitId));

    if (!hasConfiguredHours) {
      return t("studio.agenda.chrome.daySummaryNoHours", {
        weekday,
        date: dayMonth,
        countLabel,
      });
    }

    return t("studio.agenda.chrome.daySummary", {
      weekday,
      date: dayMonth,
      start: formatSlotBoundLabel(calendarSlotBounds.slotMinTime),
      end: formatSlotBoundLabel(calendarSlotBounds.slotMaxTime),
      countLabel,
    });
  }, [
    calendarSelectedDate,
    calendarSlotBounds.slotMaxTime,
    calendarSlotBounds.slotMinTime,
    filterUnitId,
    i18n.language,
    isConsolidatedView,
    t,
    unitUsesDefaultSchedule,
    visibleDayAppointmentCount,
  ]);

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.agenda.breadcrumb.title")}
          pageTitle={t("studio.agenda.breadcrumb.pageTitle")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="agenda-card-header agenda-card-header--chrome">
                <div className="agenda-chrome-row agenda-chrome-row--primary">
                  <div className="agenda-card-title min-w-0">
                    <h4 className="card-title mb-0">{t("studio.agenda.breadcrumb.title")}</h4>
                    <p className="agenda-chrome-summary text-muted small mb-0 mt-1">
                      {chromeDaySummary}
                    </p>
                  </div>
                  <div className="agenda-card-toolbar">
                    <Button color="success" size="sm" onClick={handleOpenCreateForm}>
                      <i className="ri-add-line align-bottom me-1" />
                      {t("studio.agenda.actions.new")}
                    </Button>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={filterUnitId}
                      onChange={(event) => {
                        const nextUnitId = event.target.value;
                        setFilterUnitId(nextUnitId);
                        setPage(1);
                        setAgendaUnitForTenant(resolveCurrentTenantId(), nextUnitId);

                        if (canUseConsolidatedView && units.length > 1) {
                          writeConsolidatedViewPreference(nextUnitId === "");
                        }
                      }}
                      aria-label={t("studio.agenda.filters.unit")}
                      className="agenda-unit-select"
                    >
                      {canUseConsolidatedView && units.length > 1 ? (
                        <option value="">
                          {t("studio.agenda.filters.consolidatedUnits")}
                        </option>
                      ) : (
                        <option value="">{t("studio.agenda.filters.allUnits")}</option>
                      )}
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Input>
                    <AgendaOptionsMenu
                      unitSchedulePath={buildUnitSchedulePath(filterUnitId)}
                      unitScheduleDisabled={isConsolidatedView}
                      guideUrl={agendaGuideUrl}
                      lastAction={lastOptionsAction}
                      onRememberAction={handleRememberOptionsAction}
                      onOpenStatus={handleOpenStatusLegend}
                      onPrintDay={handlePrintDay}
                      onOpenWaitlist={() => setWaitlistOpen(true)}
                    />
                  </div>
                </div>
                <div className="agenda-chrome-row agenda-chrome-row--secondary">
                  <Nav pills className="agenda-view-tabs nav-success mb-0">
                    <NavItem>
                      <NavLink
                        className={activeTab === "calendar" ? "active" : ""}
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          setActiveTab("calendar");
                          setAgendaTabForTenant(resolveCurrentTenantId(), "calendar");

                          if (filterDate !== calendarSelectedDate) {
                            navigateCalendarToDate(filterDate);
                          }
                        }}
                      >
                        {t("studio.agenda.tabs.calendar")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "list" ? "active" : ""}
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          setActiveTab("list");
                          setAgendaTabForTenant(resolveCurrentTenantId(), "list");
                        }}
                      >
                        {t("studio.agenda.tabs.list")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "waiting" ? "active" : ""}
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          setActiveTab("waiting");
                          setAgendaTabForTenant(resolveCurrentTenantId(), "waiting");
                          setWaitingRoomRefreshKey((value) => value + 1);
                        }}
                      >
                        {t("studio.agenda.tabs.waiting")}
                        {waitingRoomBadgeCount > 0 ? (
                          <Badge color="danger" pill className="ms-1 align-middle">
                            {waitingRoomBadgeCount}
                          </Badge>
                        ) : null}
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <AgendaWeekDayStrip
                    selectedDate={calendarSelectedDate}
                    weekMode={calendarUsesWeekView}
                    onSelectDate={handleWeekStripSelect}
                    onSelectWeek={handleSelectWeekView}
                    onNavigateWeek={handleWeekStripNavigate}
                    onToday={handleCalendarToday}
                  />
                </div>
              </CardHeader>
              <CardBody>
                {isConsolidatedView ? (
                  <Alert color="info" className="mb-3">
                    {t("studio.agenda.consolidated.banner")}
                  </Alert>
                ) : null}

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="calendar">
                    {unitUsesDefaultSchedule && filterUnitId ? (
                      <Alert color="warning" className="mb-3 py-2">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                          <div className="d-flex flex-column gap-1">
                            <span>{t("studio.agenda.calendar.defaultScheduleBanner")}</span>
                            <a
                              href={unitScheduleHelpUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="small fw-medium"
                            >
                              <i className="ri-question-line align-bottom me-1" />
                              {t("studio.agenda.calendar.defaultScheduleHelpLink")}
                            </a>
                          </div>
                          <Button
                            color="warning"
                            size="sm"
                            outline
                            tag={Link}
                            to={buildUnitSchedulePath(filterUnitId)}
                          >
                            {t("studio.agenda.calendar.defaultScheduleAction")}
                          </Button>
                        </div>
                      </Alert>
                    ) : null}
                    {closedCalendarDates.has(calendarSelectedDate) ? (
                      <Alert color="secondary" className="mb-3 py-2">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                          <span>
                            {closedCalendarDayMeta.get(calendarSelectedDate)?.reason
                              ? t("studio.agenda.calendar.closedDayBannerNamed", {
                                  reason:
                                    closedCalendarDayMeta.get(calendarSelectedDate)?.reason,
                                })
                              : t("studio.agenda.calendar.closedDayBanner")}
                          </span>
                          <Button
                            color="secondary"
                            size="sm"
                            outline
                            tag={Link}
                            to={
                              closedCalendarDayMeta.get(calendarSelectedDate)?.exceptionId
                                ? `/settings/calendar-exceptions?exception_id=${
                                    closedCalendarDayMeta.get(calendarSelectedDate)
                                      ?.exceptionId
                                  }&date=${calendarSelectedDate}`
                                : `/settings/calendar-exceptions?date=${calendarSelectedDate}`
                            }
                          >
                            {t("studio.agenda.calendar.closedDayAction")}
                          </Button>
                        </div>
                      </Alert>
                    ) : null}
                    {professionals.length === 0 ? (
                      <Alert color="warning" className="mb-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                          <span>{t("studio.agenda.calendar.noProfessionalsBanner")}</span>
                          <Button color="warning" size="sm" outline tag={Link} to="/professionals">
                            {t("studio.agenda.calendar.noProfessionalsAction")}
                          </Button>
                        </div>
                      </Alert>
                    ) : null}
                    <div className="agenda-calendar-toolbar d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                      <AgendaProfessionalFilterDropdown
                        professionals={professionals}
                        selectedProfessionalIds={selectedProfessionalIds}
                        showInactiveOnCalendar={showInactiveOnCalendar}
                        onToggleProfessional={toggleProfessionalSelection}
                        onSelectAll={selectAllProfessionals}
                        onSelectNone={selectNoProfessionals}
                        onToggleShowInactive={setShowInactiveOnCalendar}
                      />
                    </div>
                    {useResourceColumns && resourcePageCount > 1 ? (
                      <div className="agenda-resource-pagination d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                        <span className="text-muted small">
                          {t("studio.agenda.calendar.resourcePageSummary", {
                            page: clampedResourcePageIndex + 1,
                            total: resourcePageCount,
                          })}
                        </span>
                        <div className="d-flex gap-1">
                          <Button
                            color="light"
                            size="sm"
                            disabled={clampedResourcePageIndex <= 0}
                            onClick={() =>
                              handleResourcePageChange(Math.max(0, clampedResourcePageIndex - 1))
                            }
                            aria-label={t("studio.agenda.calendar.resourcePagePrevious")}
                          >
                            <i className="ri-arrow-left-s-line" aria-hidden="true" />
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={clampedResourcePageIndex >= resourcePageCount - 1}
                            onClick={() =>
                              handleResourcePageChange(
                                Math.min(resourcePageCount - 1, clampedResourcePageIndex + 1)
                              )
                            }
                            aria-label={t("studio.agenda.calendar.resourcePageNext")}
                          >
                            <i className="ri-arrow-right-s-line" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                    ) : null}
                    <AgendaStatusLegend
                      resolveStatusLabel={resolveStatusLabel}
                      open={statusLegendOpen}
                      onOpenChange={setStatusLegendOpen}
                    />
                    <div className="agenda-calendar-wrapper agenda-printable position-relative mt-2">
                      {(loadingCalendar || loadingCalendarBounds) ? (
                        <div className="agenda-calendar-loading">
                          <Spinner color="primary">{t("studio.agenda.loading")}</Spinner>
                        </div>
                      ) : null}
                      <AgendaCustomGrid
                        ref={gridRef}
                        mode={calendarUsesWeekView ? "week" : "day"}
                        selectedDate={calendarSelectedDate}
                        columns={gridColumns}
                        blocks={agendaGridBlocks}
                        bounds={{
                          slotMinTime: calendarSlotBounds.slotMinTime,
                          slotMaxTime: calendarSlotBounds.slotMaxTime,
                        }}
                        scrollTime={calendarSlotBounds.scrollTime}
                        showNowIndicator={
                          calendarUsesWeekView
                            ? gridColumns.some((column) => column.isToday)
                            : calendarSelectedDate === todayIsoDate()
                        }
                        selectedAppointmentId={selectedCalendarAppointment?.id ?? null}
                        resolveStatusLabel={resolveStatusLabel}
                        showProfessionalInTooltip={isConsolidatedView || calendarUsesWeekView}
                        onSlotClick={handleGridSlotClick}
                        onEventClick={handleCalendarEventClick}
                        onFillHealthRecord={openHealthRecordForAppointment}
                        onViewEvolution={openViewEvolutionForAppointment}
                        onClearArrived={
                          allowMarkArrived
                            ? (appointment) => void handleClearArrivedFromGrid(appointment)
                            : undefined
                        }
                        onEventDrop={handleGridEventDrop}
                        onColumnHeaderClick={
                          calendarUsesWeekView ? handleWeekColumnHeaderClick : undefined
                        }
                        onAbsenceClick={
                          calendarUsesWeekView
                            ? undefined
                            : (columnId) => {
                                const professional = professionals.find(
                                  (item) => String(item.id) === columnId
                                );
                                const range = defaultAbsenceRange(calendarSelectedDate);

                                if (!professional) {
                                  return;
                                }

                                setAbsenceTarget({
                                  professional,
                                  startLocal: range.startLocal,
                                  endLocal: range.endLocal,
                                });
                              }
                        }
                      />
                    </div>
                  </TabPane>

                  <TabPane tabId="list">
                    <div className="agenda-list-toolbar d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <p className="text-muted mb-0">{formatDateLabel(filterDate)}</p>
                        <div className="form-check form-check-inline mb-0 align-self-center">
                          <Input
                            id={listOnlyWithExtrasId}
                            type="checkbox"
                            className="form-check-input"
                            checked={listOnlyWithExtras}
                            onChange={(event) =>
                              setListOnlyWithExtras(event.target.checked)
                            }
                          />
                          <label
                            className="form-check-label text-nowrap"
                            htmlFor={listOnlyWithExtrasId}
                          >
                            {t("studio.agenda.filters.onlyWithExtrasCount", {
                              count: listAppointmentsWithExtrasCount,
                            })}
                          </label>
                        </div>
                        <div className="form-check form-check-inline mb-0 align-self-center">
                          <Input
                            id={listLateOnlyId}
                            type="checkbox"
                            className="form-check-input"
                            checked={listLateOnly}
                            onChange={(event) =>
                              setListLateOnly(event.target.checked)
                            }
                          />
                          <label
                            className="form-check-label text-nowrap"
                            htmlFor={listLateOnlyId}
                          >
                            {t("studio.agenda.filters.lateOnlyCount", {
                              count: listLateAppointmentsCount,
                            })}
                          </label>
                        </div>
                      </div>
                      {lateArrivableAppointments.length > 0 ? (
                        <Button
                          color="info"
                          size="sm"
                          outline
                          disabled={batchArriving || statusUpdatingId !== null}
                          title={t("studio.agenda.actions.arrived.batchHint")}
                          onClick={() => void handleBatchMarkArrived()}
                        >
                          {batchArriving ? (
                            <Spinner size="sm" className="me-1" />
                          ) : (
                            <i className="ri-walk-line me-1" aria-hidden="true" />
                          )}
                          {t("studio.agenda.actions.arrived.batch")}
                          <Badge color="light" className="ms-1 text-body">
                            {lateArrivableAppointments.length}
                          </Badge>
                        </Button>
                      ) : null}
                    </div>
                    {loadingList ? (
                      <div className="text-center py-5">
                        <Spinner color="primary">{t("studio.agenda.loading")}</Spinner>
                      </div>
                    ) : listAppointmentsData.length === 0 ? (
                      <div className="text-center py-5">
                        <p className="text-muted mb-3">{t("studio.agenda.empty.noData")}</p>
                        <Button color="primary" onClick={handleOpenCreateForm}>
                          {t("studio.agenda.actions.createFirst")}
                        </Button>
                      </div>
                    ) : filteredListAppointments.length === 0 ? (
                      <div className="text-center py-5">
                        <p className="text-muted mb-3">
                          {listLateOnly && !listOnlyWithExtras
                            ? t("studio.agenda.empty.noLateMatch")
                            : t("studio.agenda.empty.noExtrasMatch")}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table className="align-middle table-nowrap mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>{t("studio.agenda.table.time")}</th>
                                <th>{t("studio.agenda.table.client")}</th>
                                <th>{t("studio.agenda.table.professional")}</th>
                                <th>{t("studio.agenda.table.service")}</th>
                                <th>{t("studio.agenda.table.unit")}</th>
                                <th>{t("studio.agenda.table.status")}</th>
                                <th className="text-end">{t("studio.agenda.table.actions")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredListAppointments.map((appointment) => (
                                <tr key={appointment.id}>
                                  <td className="fw-medium">
                                    {formatWallClockTime(appointment.start_datetime)}
                                    <span className="text-muted">
                                      {" "}
                                      – {formatWallClockTime(appointment.end_datetime)}
                                    </span>
                                  </td>
                                  <td>
                                    {appointment.client?.name ?? notAvailable}
                                    {canHealthRecord &&
                                    appointmentNeedsHealthRecord(appointment) ? (
                                      <Badge
                                        color="warning"
                                        className="ms-1 text-dark align-middle"
                                        role="button"
                                        style={{ cursor: "pointer" }}
                                        title={t("studio.agenda.badges.noHealthRecordHint")}
                                        onClick={() => openHealthRecordForAppointment(appointment)}
                                      >
                                        {t("studio.agenda.badges.noHealthRecord")}
                                      </Badge>
                                    ) : null}
                                    {canHealthRecord && appointment.has_clinical_gate_alert ? (
                                      <Badge
                                        color="danger"
                                        className="ms-1 align-middle"
                                        role="button"
                                        style={{ cursor: "pointer" }}
                                        title={t("studio.agenda.badges.clinicalGateHint")}
                                        onClick={() => openHealthRecordForAppointment(appointment)}
                                      >
                                        <i className="ri-alert-line me-1" aria-hidden="true" />
                                        {t("studio.agenda.badges.clinicalGate")}
                                      </Badge>
                                    ) : null}
                                    {canHealthRecord && appointment.needs_consent ? (
                                      <Badge
                                        color="danger"
                                        className="ms-1 align-middle"
                                        role="button"
                                        style={{ cursor: "pointer" }}
                                        title={t("studio.agenda.badges.needsConsentHint")}
                                        onClick={() => openHealthRecordForAppointment(appointment)}
                                      >
                                        {t("studio.agenda.badges.needsConsent")}
                                      </Badge>
                                    ) : null}
                                    {canHealthRecord &&
                                    appointment.evolution_photos_expiring_soon ? (
                                      <Badge
                                        color="warning"
                                        className="ms-1 text-dark align-middle"
                                        role="button"
                                        style={{ cursor: "pointer" }}
                                        title={
                                          appointment.evolution_photos_expire_in_days === 0
                                            ? t(
                                                "studio.agenda.badges.evolutionPhotosExpireTodayHint"
                                              )
                                            : t(
                                                "studio.agenda.badges.evolutionPhotosExpiringHint",
                                                {
                                                  days:
                                                    appointment.evolution_photos_expire_in_days ??
                                                    0,
                                                }
                                              )
                                        }
                                        onClick={() =>
                                          openViewEvolutionForAppointment(appointment)
                                        }
                                      >
                                        <i
                                          className="ri-timer-line me-1"
                                          aria-hidden="true"
                                        />
                                        {appointment.evolution_photos_expire_in_days === 0
                                          ? t(
                                              "studio.agenda.badges.evolutionPhotosExpireToday"
                                            )
                                          : t(
                                              "studio.agenda.badges.evolutionPhotosExpiring",
                                              {
                                                days:
                                                  appointment.evolution_photos_expire_in_days ??
                                                  0,
                                              }
                                            )}
                                      </Badge>
                                    ) : null}
                                  </td>
                                  <td>{appointment.professional?.name ?? notAvailable}</td>
                                  <td>
                                    {isBundleAppointment(appointment) ? (
                                      <Badge color="info" className="me-1">
                                        {t("studio.agenda.calendar.bundleBadge")}
                                      </Badge>
                                    ) : null}
                                    {appointment.service?.name ?? notAvailable}
                                    {(appointment.extra_services ?? []).length > 0 ? (
                                      <>
                                        <Badge
                                          color="soft-secondary"
                                          className="ms-1 align-middle"
                                          title={t("studio.agenda.calendar.extrasTooltip", {
                                            count: appointment.extra_services!.length,
                                            names: [...(appointment.extra_services ?? [])]
                                              .sort(
                                                (left, right) =>
                                                  (left.sort_order ?? 0) -
                                                  (right.sort_order ?? 0)
                                              )
                                              .map((item, index) =>
                                                t(
                                                  "studio.agenda.detail.extraServicesOrdered",
                                                  {
                                                    position: index + 1,
                                                    name:
                                                      item.name ?? `#${item.id}`,
                                                  }
                                                )
                                              )
                                              .join(" · "),
                                          })}
                                        >
                                          {t("studio.agenda.calendar.extrasBadge", {
                                            count: appointment.extra_services!.length,
                                          })}
                                        </Badge>
                                        <span className="text-muted small d-block">
                                          {[...(appointment.extra_services ?? [])]
                                            .sort(
                                              (left, right) =>
                                                (left.sort_order ?? 0) -
                                                (right.sort_order ?? 0)
                                            )
                                            .map((item, index) =>
                                              t(
                                                "studio.agenda.detail.extraServicesOrdered",
                                                {
                                                  position: index + 1,
                                                  name:
                                                    item.name ?? `#${item.id}`,
                                                }
                                              )
                                            )
                                            .join(" · ")}
                                        </span>
                                      </>
                                    ) : null}
                                    {getDisplayableAppointmentNotes(appointment.notes) ? (
                                      <span
                                        className="text-body-secondary small d-block text-truncate"
                                        style={{ maxWidth: "16rem" }}
                                        title={getDisplayableAppointmentNotes(appointment.notes) ?? undefined}
                                      >
                                        <i
                                          className="ri-sticky-note-line me-1"
                                          aria-hidden="true"
                                        />
                                        {getDisplayableAppointmentNotes(appointment.notes)}
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>{appointment.unit?.name ?? notAvailable}</td>
                                  <td>
                                    <Badge
                                      color={resolveStatusColor(appointment.status)}
                                      className="text-uppercase"
                                    >
                                      {resolveStatusLabel(appointment.status)}
                                    </Badge>
                                    {appointment.client_arrived_at ? (
                                      <Badge color="success" className="ms-1">
                                        {t("studio.agenda.badges.arrived")}
                                      </Badge>
                                    ) : null}
                                    {appointment.status === "pending_deposit" ? (
                                      <Badge color="warning" className="ms-1">
                                        {t("studio.agenda.calendar.pendingDepositBadge")}
                                      </Badge>
                                    ) : null}
                                  </td>
                                  <td className="text-end">
                                    {isActionableStatus(appointment.status) ? (
                                      <div className="d-flex flex-wrap justify-content-end gap-1">
                                        {allowMarkArrived &&
                                        ["scheduled", "confirmed"].includes(appointment.status) &&
                                        !appointment.client_arrived_at ? (
                                          <Button
                                            color="info"
                                            size="sm"
                                            outline
                                            disabled={statusUpdatingId === appointment.id}
                                            onClick={() => void handleMarkArrived(appointment)}
                                          >
                                            {statusUpdatingId === appointment.id ? (
                                              <Spinner size="sm" className="me-1" />
                                            ) : null}
                                            {t("studio.agenda.actions.arrived.label")}
                                          </Button>
                                        ) : null}
                                        {allowConclude ? (
                                          <Button
                                            color="success"
                                            size="sm"
                                            outline
                                            disabled={statusUpdatingId === appointment.id}
                                            onClick={() => void requestConcludeAppointment(appointment)}
                                          >
                                            {statusUpdatingId === appointment.id ? (
                                              <Spinner size="sm" className="me-1" />
                                            ) : null}
                                            {t("studio.agenda.actions.conclude.label")}
                                          </Button>
                                        ) : null}
                                        <Button
                                          color="warning"
                                          size="sm"
                                          outline
                                          disabled={statusUpdatingId === appointment.id}
                                          onClick={() => setNoShowTarget(appointment)}
                                        >
                                          {t("studio.agenda.actions.noShow.label")}
                                        </Button>
                                        <Button
                                          color="danger"
                                          size="sm"
                                          outline
                                          disabled={statusUpdatingId === appointment.id}
                                          onClick={() => setCancelTarget(appointment)}
                                        >
                                          {t("studio.agenda.actions.cancelAppointment.label")}
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        {meta && paginationLastPage > 1 && (
                          <Row className="align-items-center mt-3">
                            <Col sm={6}>
                              <div className="text-muted">
                                {t("studio.agenda.pagination.showing", {
                                  from: paginationFrom,
                                  to: paginationTo,
                                  total: paginationTotal,
                                })}
                              </div>
                            </Col>
                            <Col sm={6}>
                              <Pagination className="pagination-separated justify-content-sm-end mb-0">
                                <PaginationItem disabled={page <= 1}>
                                  <PaginationLink
                                    previous
                                    href="#"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      if (page > 1) {
                                        setPage(page - 1);
                                      }
                                    }}
                                  >
                                    {t("studio.agenda.pagination.previous")}
                                  </PaginationLink>
                                </PaginationItem>
                                <PaginationItem disabled={page >= paginationLastPage}>
                                  <PaginationLink
                                    next
                                    href="#"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      if (page < paginationLastPage) {
                                        setPage(page + 1);
                                      }
                                    }}
                                  >
                                    {t("studio.agenda.pagination.next")}
                                  </PaginationLink>
                                </PaginationItem>
                              </Pagination>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </TabPane>
                  <TabPane tabId="waiting">
                    <WaitingRoomPanel
                      unitId={filterUnitId ? Number(filterUnitId) : null}
                      date={filterDate}
                      canManage={allowMarkArrived}
                      refreshKey={waitingRoomRefreshKey}
                      onOpenTv={() => setWaitingRoomTvOpen(true)}
                      onWaitingCountChange={setWaitingRoomBadgeCount}
                    />
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <WaitingRoomTvOverlay
        open={waitingRoomTvOpen}
        unitId={filterUnitId ? Number(filterUnitId) : null}
        date={filterDate}
        onClose={() => setWaitingRoomTvOpen(false)}
      />

      <AppointmentDetailOffcanvas
        isOpen={selectedCalendarAppointment !== null}
        appointment={selectedCalendarAppointment}
        statusUpdatingId={statusUpdatingId}
        notAvailable={notAvailable}
        resolveStatusLabel={resolveStatusLabel}
        resolveStatusColor={resolveStatusColor}
        isActionableStatus={isActionableStatus}
        formatTimeRange={formatTimeRange}
        formatDateLabel={formatDateLabel}
        resolveProfessionalColor={resolveProfessionalColor}
        onClose={() => setSelectedCalendarAppointment(null)}
        onConclude={(appointment) => void requestConcludeAppointment(appointment)}
        onNoShow={(appointment) => setNoShowTarget(appointment)}
        onCancel={(appointment) => setCancelTarget(appointment)}
        onConfirmDeposit={handleConfirmManualDeposit}
        onEdit={handleEditAppointment}
        onMarkArrived={(appointment) => void handleMarkArrived(appointment)}
        onClearArrived={(appointment) => void handleClearArrived(appointment)}
        onFillHealthRecord={openHealthRecordForAppointment}
        onRegisterEvolution={openRegisterEvolutionForAppointment}
        onViewEvolution={openViewEvolutionForAppointment}
        onShowDepositPix={(pix) => {
          pixPaymentConfirmedRef.current = false;
          setPixModalData({
            appointmentId: pix.appointmentId,
            qrCodeBase64: pix.qrCodeBase64,
            copyPasteString: pix.copyPasteString,
          });
        }}
      />

      <ClientAnamnesisOffcanvas
        isOpen={anamnesisTarget !== null}
        onClose={() => setAnamnesisTarget(null)}
        clientId={anamnesisTarget?.id ?? 0}
        clientName={anamnesisTarget?.name ?? ""}
        serviceId={anamnesisTarget?.serviceId ?? null}
        initialTemplateId={anamnesisTarget?.initialTemplateId ?? null}
        evolutionAppointmentId={anamnesisTarget?.evolutionAppointmentId ?? null}
        initialRecordId={anamnesisTarget?.initialRecordId ?? null}
        initialRecordTypeFilter={anamnesisTarget?.initialRecordTypeFilter ?? "all"}
        onRecordSaved={handleAnamnesisRecordSaved}
      />

      <AppointmentFormOffcanvas
        key={
          formMode === "edit" && editingAppointment
            ? `edit-${editingAppointment.id}`
            : createPrefill
              ? `create-${createPrefill.date ?? ""}-${createPrefill.professional_id ?? ""}-${createPrefill.preferred_start ?? ""}-${createPrefill.service_id ?? ""}-${createPrefill.unit_id ?? ""}`
              : formInitialClientId ?? "agenda-new-appointment"
        }
        isOpen={formOpen}
        mode={formMode}
        editingAppointment={editingAppointment}
        onClose={handleFormClose}
        onSuccess={handleAppointmentSaved}
        initialClientId={formInitialClientId}
        createPrefill={createPrefill}
        onRegisterAbsence={({ professionalId, startLocal, endLocal }) => {
          const professional = professionals.find((item) => String(item.id) === professionalId);

          if (!professional) {
            return;
          }

          setFormOpen(false);
          setAbsenceTarget({ professional, startLocal, endLocal });
        }}
        onPixCreated={(pix) => {
          pixPaymentConfirmedRef.current = false;
          setPixModalData({
            appointmentId: pix.appointmentId,
            qrCodeBase64: pix.qrCodeBase64,
            copyPasteString: pix.copyPasteString,
          });
        }}
      />

      <ScheduleBlocksModal
        professional={absenceTarget?.professional ?? null}
        isOpen={absenceTarget !== null}
        initialRange={
          absenceTarget
            ? { startLocal: absenceTarget.startLocal, endLocal: absenceTarget.endLocal }
            : null
        }
        onClose={() => setAbsenceTarget(null)}
      />

      <Modal isOpen={concludeTarget !== null} toggle={() => setConcludeTarget(null)} centered>
        <ModalHeader toggle={() => setConcludeTarget(null)}>
          {t("studio.agenda.actions.complete_confirmTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">{t("studio.agenda.actions.complete_confirm")}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConcludeTarget(null)}>
            {t("studio.agenda.actions.complete_dismiss")}
          </Button>
          <Button
            color="success"
            onClick={handleConcludeAppointment}
            disabled={statusUpdatingId !== null}
          >
            {statusUpdatingId !== null ? (
              <Spinner size="sm" className="me-1" />
            ) : null}
            {t("studio.agenda.actions.conclude.label")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={cancelTarget !== null} toggle={() => setCancelTarget(null)} centered>
        <ModalHeader toggle={() => setCancelTarget(null)}>
          {t("studio.agenda.actions.cancelAppointment.confirmTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">{t("studio.agenda.actions.cancelAppointment.confirm")}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCancelTarget(null)}>
            {t("studio.agenda.actions.cancelAppointment.dismiss")}
          </Button>
          <Button
            color="danger"
            onClick={handleCancelAppointment}
            disabled={statusUpdatingId !== null}
          >
            {statusUpdatingId !== null ? (
              <Spinner size="sm" className="me-1" />
            ) : null}
            {t("studio.agenda.actions.cancelAppointment.label")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={noShowTarget !== null} toggle={() => setNoShowTarget(null)} centered>
        <ModalHeader toggle={() => setNoShowTarget(null)}>
          {t("studio.agenda.actions.noShow.confirmTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">{t("studio.agenda.actions.noShow.confirm")}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setNoShowTarget(null)}>
            {t("studio.agenda.actions.noShow.dismiss")}
          </Button>
          <Button
            color="warning"
            onClick={handleNoShowAppointment}
            disabled={statusUpdatingId !== null}
          >
            {statusUpdatingId !== null ? (
              <Spinner size="sm" className="me-1" />
            ) : null}
            {t("studio.agenda.actions.noShow.label")}
          </Button>
        </ModalFooter>
      </Modal>

      <PixPaymentModal
        isOpen={pixModalData !== null}
        onClose={handlePixModalClose}
        appointmentId={pixModalData?.appointmentId ?? null}
        qrCodeBase64={pixModalData?.qrCodeBase64 ?? ""}
        copyPasteString={pixModalData?.copyPasteString ?? ""}
        onPaymentConfirmed={handlePixPaymentConfirmed}
      />

      <WaitlistOffcanvas
        isOpen={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        defaultUnitId={filterUnitId || String(units[0]?.id ?? "")}
        units={units}
      />
    </div>
  );
};

export default AgendaList;
